"use client";

import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { User, MapPin, Sparkles, BookOpen } from "lucide-react";

export function EditorMentionDemo() {
  const [step, setStep] = useState(0);
  const [charsTyped, setCharsTyped] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  const DEMO_TEXT = "The hero approached ";

  useEffect(() => {
    cancelledRef.current = false;

    const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

    const runAnimation = async () => {
      const isCancelled = () => cancelledRef.current;

      // Small initial delay
      await wait(800);
      if (isCancelled()) return;

      // Typing phase
      for (let i = 1; i <= DEMO_TEXT.length; i++) {
        if (isCancelled()) return;
        setCharsTyped(i);
        await wait(Math.random() * 40 + 30);
      }
      if (isCancelled()) return;
      setStep(1);
      await wait(500);

      if (isCancelled()) return; setStep(2); // @ typed
      await wait(600);
      if (isCancelled()) return; setStep(3); // @E typed
      await wait(200);
      if (isCancelled()) return; setStep(4); // @El typed
      await wait(600);
      if (isCancelled()) return; setStep(5); // Mouse moves to list
      await wait(900);
      if (isCancelled()) return; setStep(6); // Item clicked
      await wait(400);
      if (isCancelled()) return; setStep(7); // Mouse moves to chip
      await wait(700);
      if (isCancelled()) return; setStep(8); // Tooltip opens
      await wait(5000); // Hold for reading

      if (isCancelled()) return;
      // Reset and loop
      setStep(0);
      setCharsTyped(0);
      await wait(2000);
      if (!isCancelled()) {
        runAnimation();
      }
    };

    runAnimation();

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const currentText = DEMO_TEXT.substring(0, charsTyped);

  return (
    <section 
      ref={containerRef}
      className="mx-auto w-full max-w-6xl px-6 pt-24 pb-12 lg:px-12 flex flex-col md:flex-row-reverse gap-12 items-center justify-between"
    >
      {/* Editor Canvas Demo Container */}
      <div className="relative w-full max-w-2xl flex-1 rounded-3xl border border-white/10 bg-gradient-to-bl from-indigo-900/10 via-background to-purple-900/10 shadow-2xl p-4 overflow-hidden before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/5 before:via-transparent before:to-transparent before:pointer-events-none">
        
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />

        <div className="bg-[#f8f9fa] dark:bg-zinc-950/80 w-full h-[380px] overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-inner relative font-sans flex flex-col">
          {/* Editor Header Fake */}
          <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 p-3 flex gap-4 items-center px-4">
             <div className="flex gap-2">
                 <div className="h-3 w-3 rounded-full bg-red-400"></div>
                 <div className="h-3 w-3 rounded-full bg-amber-400"></div>
                 <div className="h-3 w-3 rounded-full bg-green-400"></div>
             </div>
             <div className="flex-1 text-center text-xs font-semibold text-zinc-400">Chapter 1</div>
             <div className="flex gap-2 text-zinc-300">
                <BookOpen size={14} />
             </div>
          </div>

          {/* Paper Canvas */}
          <div className="flex-1 overflow-visible relative p-6 bg-white dark:bg-zinc-900 m-6 rounded shadow-sm border border-zinc-100 dark:border-zinc-800">
              
              <div className="text-zinc-800 dark:text-zinc-200 text-lg leading-relaxed relative flex items-center flex-wrap gap-[0.2em]">
                 <span>{currentText}</span>
                 
                 {/* Typing span for @mentions */}
                 {step >= 2 && step <= 5 && (
                     <span className="text-zinc-400 relative">
                        @{step === 3 ? "E" : step >= 4 ? "El" : ""}
                        <span className="animate-pulse absolute top-1/2 -translate-y-1/2 right-[-2px] w-[2px] h-5 bg-black dark:bg-white ml-0.5" />
                     </span>
                 )}

                 {/* Completed mention chip */}
                 {step >= 6 && (
                     <span className={cn(
                        "relative group bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded-md inline-flex items-center gap-1.5 font-medium ring-1 ring-indigo-200 dark:ring-indigo-700/50 shadow-sm transition-all duration-300",
                        step >= 7 && "ring-indigo-400 dark:ring-indigo-500 bg-indigo-200 dark:bg-indigo-800/60" // Highlight when mouse hits it
                     )}>
                         <User size={14} /> Elandor
                         
                         {/* Fake Tooltip Popover */}
                         <div className={cn(
                             "absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[280px] z-20 p-4 bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-2xl text-sm transition-all duration-300", 
                             step >= 8 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
                         )}>
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 border-x-8 border-x-transparent border-b-8 border-b-white dark:border-b-zinc-900 drop-shadow-sm"></div>
                            
                            <div className="flex gap-3 items-start border-b border-zinc-100 dark:border-zinc-800 pb-3 mb-3">
                                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 shrink-0">
                                   <User size={18} className="text-indigo-600 dark:text-indigo-400"/> 
                                </div>
                                <div>
                                    <div className="font-bold text-foreground">Elandor</div>
                                    <div className="text-xs font-semibold tracking-wider text-indigo-500 uppercase mt-0.5">Character</div>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                The brave knight of the realm. A skilled swordsman sworn to protect the King, known for his unwavering loyalty and mysterious past.
                            </p>
                         </div>
                     </span>
                 )}

                 {/* Show cursor when not mentioning */}
                 {(step === 1 || step === 0) && charsTyped === DEMO_TEXT.length && (
                     <span className="animate-pulse w-[2px] h-5 bg-black dark:bg-white inline-block -ml-1 border-box" />
                 )}
              </div>

              {/* Mention Suggestion Dropdown */}
              {step >= 2 && step <= 5 && (
                  <div className={cn(
                      "absolute top-[60px] left-[180px] w-64 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-2xl z-10 transition-all duration-300 overflow-hidden", 
                      step >= 2 ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-2 scale-95"
                  )}>
                      <div className="text-[10px] font-bold tracking-wider text-zinc-400 px-3 py-2 bg-zinc-50 dark:bg-zinc-900/50 uppercase border-b border-zinc-100 dark:border-zinc-800/80">
                           Link Lore Entry
                      </div>
                      <div className="p-1.5 flex flex-col gap-0.5">
                          <div className={cn(
                              "flex items-center justify-between gap-3 px-2 py-2 rounded-lg text-sm cursor-pointer transition-colors", 
                              step === 5 ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-zinc-100 dark:hover:bg-zinc-800"
                          )}>
                              <div className="flex items-center gap-2">
                                <User size={14} className="text-indigo-500" />
                                <span className={cn("font-medium", step === 5 && "text-indigo-700 dark:text-indigo-300")}>Elandor</span>
                              </div>
                              <span className="text-[10px] bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded">Character</span>
                          </div>
                          {(step < 4) && (
                            <>
                              <div className="flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm cursor-pointer">
                                  <div className="flex items-center gap-2">
                                    <User size={14} className="text-indigo-500" />
                                    <span className="text-zinc-600 dark:text-zinc-300">Elysia</span>
                                  </div>
                              </div>
                              <div className="flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm cursor-pointer">
                                  <div className="flex items-center gap-2">
                                    <MapPin size={14} className="text-purple-500" />
                                    <span className="text-zinc-600 dark:text-zinc-300">Emerald City</span>
                                  </div>
                              </div>
                            </>
                          )}
                      </div>
                  </div>
              )}

              {/* Fake Mouse Cursor */}
              <div 
                className={cn(
                    "absolute z-50 transition-all ease-in-out pointer-events-none drop-shadow-lg",
                    // Durations match the delays in code
                    step === 5 ? "duration-[900ms]" :
                    step === 6 ? "duration-[200ms]" :
                    step === 7 ? "duration-[700ms]" : "duration-0",

                    step <= 4 && "top-[150%] left-[120%] opacity-0",
                    step === 5 && "top-[100px] left-[260px] opacity-100", // Hover over list item
                    step === 6 && "top-[100px] left-[260px] opacity-100 scale-75", // Click (shrink)
                    step === 7 && "top-[40px] left-[240px] opacity-100 scale-100", // Hover over chip
                    step >= 8 && "top-[40px] left-[240px] opacity-100 scale-100", // Stay on chip
                    step === 0 && "opacity-0"
                )}
              >
                 <svg 
                   width="32" 
                   height="32" 
                   viewBox="0 0 24 24" 
                   fill="black"
                   className="text-black stroke-white stroke-[1.5]"
                 >
                   <path d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.8c.45 0 .67-.54.35-.85L6.35 2.86a.5.5 0 0 0-.85.35Z" />
                 </svg>
              </div>
          </div>
        </div>
      </div>

      {/* Text Explainer Section */}
      <div className="flex-1 max-w-lg space-y-6">
        <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          Write without breaking flow
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Never break your writing flow to look up lore. Simply type <kbd className="font-mono bg-muted px-1.5 py-0.5 rounded-md text-sm border font-bold">@</kbd> to instantly reference characters, locations, or items from your Wiki right in the editor.
        </p>
        
        <ul className="space-y-4 mt-8">
          <li 
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 2 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 bg-indigo-500/20 text-indigo-400 p-1 rounded-full shrink-0">
              <Sparkles size={16} />
            </div>
            <div>
              <span className="font-semibold text-foreground">Inline Context:</span> View background information, stats, or images about your entities right inside the editor via tooltips.
            </div>
          </li>
          <li 
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 6 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 bg-purple-500/20 text-purple-400 p-1 rounded-full shrink-0">
              <BookOpen size={16} />
            </div>
            <div>
              <span className="font-semibold text-foreground">Stay Consistent:</span> Avoid plot holes. With all lore seamlessly accessible, you won&apos;t have to pause writing to check your notes.
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}
